/**
 * InfinitSales - Chat List Component (Simplified)
 * WhatsApp Web-style list
 * Data comes pre-normalized from backend
 */
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { agentAnswersInList, createdAtOf } from '../../utils/autopilotScope';
import { FixedSizeList, areEqual } from 'react-window';
import {
  Search, Users, User, Archive, Check, CheckCheck,
  Image, Mic, Video, FileText, Clock, Pin, BellOff, ChevronDown, Bot, UserCheck, Loader2,
  Filter, Sticker, MapPin, BarChart3, Plus, GitBranch,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useSSEEvent, SSE_EVENTS } from '../../contexts/SSEContext';
import chatUtils from '../../utils/wa-personal/chat_utils';
import { brPhoneVariants } from '../../utils/phone';
import { matchesPhoneSearch } from '../../utils/phoneSearch';
import whatsappService from '../../services/wa-personal/whatsappService';
import authService from '../../services/global/authService';
import requestCache from '../../services/global/requestCache';
import ChatFilterDrawer from '../../components/labels/ChatFilterDrawer';
import WhoAnswersBadge from '../../components/inbox/WhoAnswersBadge';
import FunnelStageChips, { resolveStageEntries } from '../../components/labels/FunnelStageChips';
import useCustomFieldFilter from '../../hooks/useCustomFieldFilter';
import useContactAssignments from '../../hooks/useContactAssignments';
import useFeatureFlags from '../../hooks/useFeatureFlags';
import assignmentService from '../../services/global/assignmentService';
import ContactDetailsDrawer from '../../components/contacts/ContactDetailsDrawer';
import { transferidoBadge } from '../../utils/handoffBadge';

// Variantes BR do 9º dígito (55+DDD+8 <-> 55+DDD+9+8) — espelha a regra do
// backend (contact_identity.phone_candidates) para o mapa de chips de funil.
// Variantes do 9º dígito: agora em utils/phone, para o mapa de RESPONSÁVEL usar
// exatamente as mesmas chaves que o de etiqueta (a divergência entre os dois
// deixava o chip de responsável errado para sempre nos contatos com as 2 formas).

// Fixed row height for FixedSizeList. Sized to fit the worst case: chats that
// have the badge row (Transferido / Agente / Etiquetas) on top of name+preview.
// Smaller values clip the hover background and look like content is leaking.
const ROW_HEIGHT = 92;

// Linha do FixedSizeList, memoizada no padrao do react-window (itemData +
// areEqual): a linha so re-renderiza quando `style` ou `data` mudam. Tudo
// que a linha le vem por `data`, montado no ChatList com useMemo (rowData):
// qualquer coisa que a linha use e fique de fora do rowData congela a UI.
// O JSX abaixo e o mesmo da funcao inline que existia aqui antes.
const ChatRow = React.memo(function ChatRow({ index, style, data }) {
  const {
    filteredChats, hasMore, loadingMore, onLoadMore, colors, styles,
    isChatSelected, labelsByPhone, isChatHandoff, getAssignment,
    assignmentsLoading, isChatAutopilot, labelsById, funnels, onSelectChat,
    activeAgentName, getMessageStatus, getMessageIcon,
    onHoverChat,
  } = data;

  if (hasMore && index === filteredChats.length) {
    return (
      <div
        style={{
          ...style,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 8,
          color: loadingMore ? colors.textSecondary : colors.accentPrimary,
          cursor: loadingMore ? 'default' : 'pointer',
          fontSize: 14, fontWeight: 500,
          pointerEvents: loadingMore ? 'none' : 'auto',
        }}
        onClick={loadingMore ? undefined : onLoadMore}
      >
        {loadingMore ? (
          <>
            <Loader2
              size={16}
              style={{ animation: 'chatlist-spin 0.8s linear infinite' }}
            />
            Carregando conversas...
          </>
        ) : (
          <>
            <ChevronDown size={18} />
            Carregar mais conversas
          </>
        )}
      </div>
    );
  }

  const chat = filteredChats[index];
  const isSelected = isChatSelected(chat);
  const displayName = chatUtils.getChatDisplayName(chat);
  const preview = chatUtils.getMessagePreview(chat);
  const hasUnread = chat.unreadCount > 0;
  const isGroupChat = chat._isGroup || chatUtils.isGroup(chat.remoteJid);
  const phoneKey = (chat._phoneNumber || '').replace(/\D/g, '');
  const chatLabelIds = labelsByPhone[phoneKey] || [];
  const handoff = isChatHandoff(chat);
  const assignedTo = getAssignment(phoneKey);
  const transferido = transferidoBadge({
    handoff, assigned: assignedTo, loading: assignmentsLoading,
  });
  // Tag de responsável substitui a antiga tag fixa "Agente";
  // sem responsável, mantém o comportamento anterior (badge do
  // agente quando o autopilot está ativo).
  //
  // `assignmentsLoading` separa "ainda não sei" de "não tem". O
  // mapa da company demora ~2,7s para o admin; enquanto não chega,
  // TODA linha ficava com assignedTo=null e pintava o chip do
  // agente, que depois trocava sozinho pelo nome certo. Melhor não
  // afirmar nada do que afirmar errado.
  const autopilot = !handoff && !assignedTo && !assignmentsLoading
    && isChatAutopilot(chat);
  // Minimalista: em vez de listar TODAS as etiquetas (estourava a
  // altura do card), mostra só o FUNIL ao qual o contato pertence
  // (derivado da etiqueta/coluna), com reticências se for grande.
  // Chip "Funil - Etapa": um por funil com a flag multi_funnel, senão
  // o da primeira etiqueta (componente compartilhado FunnelStageChips).
  // `funnelName` só decide se a linha de selos existe.
  const funnelName = resolveStageEntries(chatLabelIds, labelsById, funnels)
    .find((e) => e.funnelName)?.funnelName || null;
  const hasBadgeRow =
    handoff || autopilot || assignedTo || !!funnelName;

  return (
    <div
      style={{
        ...style,
        ...styles.chatItem,
        // Selecionado: barra lateral verde do accent + bg verde-sutil
        // (8% de opacity). Hover fica neutro para nao confundir com
        // o estado selecionado. Padding 11px + borda 3px = 14px,
        // mantendo alinhamento do avatar nos dois estados.
        borderLeft: isSelected
          ? `3px solid ${colors.accentPrimary}`
          : '3px solid transparent',
        paddingLeft: '11px',
        transition: 'background-color 0.12s, border-color 0.12s',
        backgroundColor: isSelected ? `${colors.accentPrimary}14` : 'transparent',
      }}
      onClick={() => onSelectChat(chat)}
      // Hover via DOM (não via estado) para NÃO re-renderizar a linha
      // ao tocar — no iOS o re-render no 1º toque engolia o clique e
      // exigia 2 toques para abrir a conversa.
      onMouseEnter={(e) => {
        if (!isSelected) e.currentTarget.style.backgroundColor = colors.bgSecondary;
        // Frente C4 (flag chat_cache): o shell aquece o cache de mensagens
        // desta conversa; sem a flag a prop vem undefined e nada muda.
        onHoverChat?.(chat);
      }}
      onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}
    >
      {/* Avatar */}
      <div style={styles.avatar}>
        {chat.profilePicUrl ? (
          <img
            src={chat.profilePicUrl}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : (
          <div style={styles.avatarPlaceholder}>
            {isGroupChat ? <Users size={24} /> : <User size={24} />}
          </div>
        )}
      </div>

      {/* Chat Info */}
      <div style={styles.chatInfo}>
        {hasBadgeRow && (
          <div style={{
            display: 'flex', alignItems: 'center', flexWrap: 'nowrap',
            gap: 4, marginBottom: 2, minHeight: 18, overflow: 'hidden',
          }}>
            {transferido.show && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 3,
                backgroundColor: 'rgba(245,158,11,0.12)', color: '#b45309',
                fontSize: 10, fontWeight: 500, padding: '1px 6px',
                borderRadius: 4, flexShrink: 0, lineHeight: '16px',
              }} title={transferido.title}>
                <UserCheck size={10} />
                Transferido
              </span>
            )}
            {/* Sem responsavel (transferido para ninguem): o espaco do
                responsavel fica vazio. Nao existe mais "agente pausado": quem
                responde e o responsavel; sem responsavel, ninguem responde. */}
            {/* Um selo so para quem responde (responsavel gravado ou agente
                automatico), igual nos 5 canais: WhoAnswersBadge. */}
            <WhoAnswersBadge
              assigned={assignedTo}
              autoAgentName={autopilot ? (activeAgentName || 'Agente IA') : null}
            />
            {funnelName && (
              <FunnelStageChips
                labelIds={chatLabelIds}
                labelsById={labelsById}
                funnels={funnels}
                colors={colors}
              />
            )}
          </div>
        )}

        <div style={styles.chatHeader}>
          <span style={{
            ...styles.chatName,
            display: 'flex', alignItems: 'center', gap: 6,
            overflow: 'hidden', minWidth: 0,
          }}>
            <span style={{
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {displayName}
            </span>
            {chat.pinned && (
              <Pin size={14} style={{ color: colors.textTertiary, flexShrink: 0 }} />
            )}
          </span>
          <span style={{ ...styles.chatTime, ...(hasUnread && { color: colors.accentPrimary, fontWeight: 500 }) }}>
            {chatUtils.formatChatTime(chat.updatedAt)}
          </span>
        </div>

        <div style={styles.chatPreview}>
          {chat.lastMessage?.key?.fromMe && getMessageStatus(chat)}
          {getMessageIcon(chat.lastMessage?.message)}
          <span style={styles.chatPreviewText}>{preview || 'Clique para ver a conversa'}</span>

          {chat.muted && <BellOff size={14} style={{ color: colors.textTertiary, marginLeft: 8 }} />}
          {hasUnread && (
            <div style={styles.chatBadge}>
              {chat.unreadCount > 99 ? '99+' : chat.unreadCount}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}, areEqual);


const ChatList = ({
  // Frentes C2 e C4: hover que aquece o cache da conversa e posicao da
  // lista guardada entre montagens. Sem as flags: undefined e 0 (como hoje).
  onHoverChat,
  initialScrollOffset = 0,
  onScrollOffset,
  chats,
  selectedChat,
  onSelectChat,
  loading,
  onLoadMore,
  hasMore = false,
  loadingMore = false,
  autopilotContacts = [],
  autopilotMode = 'off',
  // Pausa do agente por canal (2026-10-14): sem selo de agente neste canal.
  channelPaused = false,
  autopilotSince = null,
  blockedContacts = [],
  // Multi-agentes: nome do agente principal — a tag de autopilot mostra o
  // NOME de quem responde, não o rótulo fixo "Agente".
  activeAgentName = null,
  handoffChats = [],
  // Filtro de etiqueta/funil: notifica o Inbox para refazer o fetch
  // server-side (paginação real por funil) em vez de filtrar só a página.
  onLabelFilterChange,
  onStartNewChat,
  onExtraFiltersChange,
  onSearchTermChange,
}) => {
  const { colors } = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  // Nova conversa (botão + dentro da busca)
  const [showNewConv, setShowNewConv] = useState(false);
  const [newConvBusy, setNewConvBusy] = useState(false);
  const [newConvError, setNewConvError] = useState(null);
  const handleNewConvSubmit = useCallback(async (phone, name) => {
    setNewConvBusy(true);
    setNewConvError(null);
    try {
      await onStartNewChat?.(phone, name);
      setShowNewConv(false);
    } catch (e) {
      setNewConvError(e?.response?.data?.detail || e?.message || 'Não foi possível iniciar a conversa.');
    } finally {
      setNewConvBusy(false);
    }
  }, [onStartNewChat]);
  const [serverResults, setServerResults] = useState([]);
  // Teto da busca global (2026-10-20): o servidor diz se ha mais que 40.
  const [serverHasMore, setServerHasMore] = useState(false);
  // Indica busca server-side em andamento (mostra spinner no input).
  const [searching, setSearching] = useState(false);
  const [filter, setFilter] = useState('all');
  const [searchFocused, setSearchFocused] = useState(false);
  const [labelsById, setLabelsById] = useState({});
  const [labelsByPhone, setLabelsByPhone] = useState({});
  const [funnels, setFunnels] = useState([]);
  const [selectedLabelFilters, setSelectedLabelFilters] = useState(new Set());
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  // Foco inicial do drawer ('funnel' quando aberto pela pílula "Funil").
  const [drawerFocus, setDrawerFocus] = useState(null);
  // Filtros extras (server-side, refetch no Inbox) + busca em mensagens.
  const [searchInMessages, setSearchInMessages] = useState(false);
  const [filterMember, setFilterMember] = useState('');   // assigned_to.id
  const [filterSince, setFilterSince] = useState('');      // 'today' | '7d' | '30d' | ''
  const [filterCreated, setFilterCreated] = useState({ from: '', to: '' }); // \"Entrou no CRM entre\" (YYYY-MM-DD)
  const [filterUnanswered, setFilterUnanswered] = useState(false);
  // Presets de data → ISO (o backend compara updatedAt >= since lexicograficamente).
  // Filtro por campo personalizado (core, compartilhado pelos 4 inboxes).
  const campos = useCustomFieldFilter();
  const notifyExtraFilters = useCallback((next) => {
    const preset = (next.since ?? filterSince) || '';
    let sinceISO = null;
    if (preset) {
      const d = new Date();
      if (preset === 'today') d.setHours(0, 0, 0, 0);
      else if (preset === '7d') d.setDate(d.getDate() - 7);
      else if (preset === '30d') d.setDate(d.getDate() - 30);
      sinceISO = d.toISOString();
    }
    onExtraFiltersChange?.({
      member_id: (next.member ?? filterMember) || null,
      since: sinceISO,
      created_from: (next.created ?? filterCreated).from || null,
      created_to: (next.created ?? filterCreated).to || null,
      unanswered: next.unanswered ?? filterUnanswered,
      unread: next.unread ?? (filter === 'unread'),
      custom_fields: next.customFields ?? campos.values,
    });
  }, [onExtraFiltersChange, filterMember, filterSince, filterCreated, filterUnanswered, campos.values, filter]);
  // Chip "Nao lidas" no servidor (2026-10-20): antes so filtrava a pagina carregada.
  const filterChipRef = useRef(filter);
  useEffect(() => {
    if (filterChipRef.current === filter) return;
    const antes = filterChipRef.current;
    filterChipRef.current = filter;
    if (antes === 'unread' || filter === 'unread') notifyExtraFilters({ unread: filter === 'unread' });
  }, [filter, notifyExtraFilters]);
  const handleChangeMember = useCallback((v) => {
    setFilterMember(v || '');
    notifyExtraFilters({ member: v || '' });
  }, [notifyExtraFilters]);
  const handleChangeSince = useCallback((v) => {
    setFilterSince(v || '');
    notifyExtraFilters({ since: v || '' });
  }, [notifyExtraFilters]);
  const handleChangeCreatedRange = useCallback((from, to) => {
    const next = { from: from || '', to: to || '' };
    setFilterCreated(next); notifyExtraFilters({ created: next });
  }, [notifyExtraFilters]);
  const handleChangeUnanswered = useCallback((v) => {
    setFilterUnanswered(!!v);
    notifyExtraFilters({ unanswered: !!v });
  }, [notifyExtraFilters]);
  // Limpa os 3 filtros extras de uma vez (evita ressuscitar via closure stale).
  const handleClearExtra = useCallback(() => {
    setFilterMember(''); setFilterSince(''); setFilterCreated({ from: '', to: '' }); setFilterUnanswered(false);
    onExtraFiltersChange?.({ member_id: null, since: null, created_from: null, created_to: null, unanswered: false });
  }, [onExtraFiltersChange]);
  // Alvos de responsável (membros + agente) para o dropdown do filtro.
  const [assignTargets, setAssignTargets] = useState([]);
  useEffect(() => {
    let alive = true;
    assignmentService.getTargets()
      .then((res) => {
        if (!alive) return;
        const list = Array.isArray(res) ? res : (res?.targets || res?.data || []);
        setAssignTargets(
          (list || [])
            .map((t) => ({ id: t.id, name: t.name || t.label || t.id, type: t.type }))
            .filter((t) => t.id && t.name)
        );
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  // Responsável por contato (membro/agente) — tag no item da lista.
  const { getAssignment, loading: assignmentsLoading } = useContactAssignments();
  // Carrega as flags da plataforma: o lote de etiquetas (authService) le
  // inbox_labels_batch_chunked de forma sincrona.
  const { flags: featureFlags } = useFeatureFlags();

  // Busca DENTRO dos filtros (INBOX_SEARCH_WITHIN_FILTERS_ENABLED): com etapa,
  // responsavel, data, campo ou "sem resposta" ativo, o termo vai para a lista
  // filtrada no servidor (`q`, intersecao) e a busca global (uniao, que ignora
  // os filtros) nao roda. Relato do Victor (myCar, 24/09): "daniel" com 2
  // etapas mostrava 41 conversas, so 1 na etapa.
  const flagBuscaNosFiltros = Boolean(featureFlags?.inbox_search_within_filters);
  const filtroNoServidor = selectedLabelFilters.size > 0 || Boolean(filterMember) || Boolean(filterSince)
    || Boolean(filterCreated.from) || Boolean(filterCreated.to) || filterUnanswered
    || (campos.values || []).length > 0
    || filter === 'unread';
  const buscaNosFiltros = flagBuscaNosFiltros && filtroNoServidor;
  // O Inbox guarda o termo (debounce igual ao da busca) e so o usa com filtro.
  useEffect(() => {
    if (!flagBuscaNosFiltros) return undefined;
    const term = (searchTerm || '').trim();
    const t = setTimeout(() => onSearchTermChange?.(term.length >= 2 ? term : '', searchInMessages), 300);
    return () => clearTimeout(t);
  }, [flagBuscaNosFiltros, searchTerm, searchInMessages, onSearchTermChange]);

  // Busca server-side: quando o termo não está na página carregada (lead
  // antigo/sem mensagem), consulta os contatos no backend e mescla no
  // resultado. B5 (2026-09-22): só dispara com 2+ caracteres e 300 ms sem
  // tecla nova (a varredura custa milhares de RU no QR da myCar). O filtro
  // local sobre a lista carregada (filteredChats) continua instantâneo.
  useEffect(() => {
    const term = (searchTerm || '').trim();
    if (term.length < 2 || buscaNosFiltros) { setServerResults([]); setSearching(false); return; }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await whatsappService.searchChats(term, searchInMessages, campos.values);
        if (!cancelled) { setServerResults(res?.data || []); setServerHasMore(Boolean(res?.has_more)); }
      } catch (e) {
        if (!cancelled) setServerResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [searchTerm, searchInMessages, campos.values, buscaNosFiltros]);

  // Troca de etiquetas no filtro: mantém o estado local (UI dos chips) E
  // notifica o Inbox para refazer o fetch server-side com as etiquetas — a
  // filtragem por funil agora é paginada de verdade (não só na página atual).
  const handleChangeLabelFilters = useCallback((next) => {
    const set = next instanceof Set ? next : new Set(next);
    setSelectedLabelFilters(set);
    onLabelFilterChange?.(Array.from(set));
  }, [onLabelFilterChange]);

  const listRef = useRef(null);
  const searchInputRef = useRef(null);
  // Ref do FixedSizeList para scrollToItem (auto-scroll do selecionado)
  const windowRef = useRef(null);
  const lastSelectedIndexRef = useRef(-1);

  // Measure list container height so FixedSizeList knows how many rows to render.
  const [listHeight, setListHeight] = useState(600);
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current;
    const update = () => setListHeight(el.clientHeight || 600);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Catálogo de etiquetas/funis (leve, cacheado) — o chip precisa do NOME e da
  // cor. Separado do mapa telefone->etiquetas, que agora vem por lote.
  const refreshLabels = useCallback(async () => {
    try {
      authService.listFunnels()
        .then((fs) => setFunnels(Array.isArray(fs) ? fs : []))
        .catch(() => {});

      const res = await authService.getLabels();
      const lista = res?.data || res || [];
      const map = {};
      (Array.isArray(lista) ? lista : []).forEach((l) => { map[l.id] = l; });
      setLabelsById(map);
      labelsByIdRef.current = map;
    } catch (err) {
      // Silent — chat list still works without badges
      console.warn('ChatList: failed to load label catalog', err);
    }
  }, []);

  useEffect(() => { refreshLabels(); }, [refreshLabels]);


  // Mudanca de etiqueta: PATCH dirigido pelo payload {phone, label_ids} do
  // SSE — o chip do cartao atualiza em O(1). Fallback quando o payload traz um
  // id que o catalogo local nao conhece (etiqueta recem-criada): recarrega SO O
  // CATALOGO, debounced. O mapa telefone->etiquetas ja veio no patch acima.
  const labelsByIdRef = useRef({});
  const labelsRefetchTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(labelsRefetchTimerRef.current), []);
  useSSEEvent(SSE_EVENTS.CONTACT_LABELS_CHANGED, (data) => {
    const phone = String(data?.phone || '').replace(/\D/g, '');
    const ids = Array.isArray(data?.label_ids) ? data.label_ids : null;
    if (phone && ids) {
      setLabelsByPhone((prev) => {
        const next = { ...prev };
        brPhoneVariants(phone).forEach((v) => {
          if (ids.length) next[v] = ids; else delete next[v];
        });
        return next;
      });
      const allKnown = ids.every((id) => labelsByIdRef.current[id]);
      if (allKnown) return;
    }
    clearTimeout(labelsRefetchTimerRef.current);
    labelsRefetchTimerRef.current = setTimeout(() => {
      // Fura o cache de 60s do catálogo: a etiqueta nova precisa entrar agora,
      // senão o chip fica sem nome/cor até o TTL vencer.
      requestCache.invalidate('labels:catalog');
      refreshLabels();
    }, 350);
  }, [refreshLabels]);

  // Keyboard shortcut for search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
      if (e.key === 'Escape' && searchTerm) {
        setSearchTerm('');
        searchInputRef.current?.blur();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [searchTerm]);

  // Check if a chat has autopilot active
  const isChatAutopilot = useCallback((chat) => {
    // Grupo nunca e atendido pelo agente.
    if (chat._isGroup || chatUtils.isGroup(chat.remoteJid)) return false;
    // Regra unica dos 5 inboxes (utils/autopilotScope), com o carimbo "so novos".
    return agentAnswersInList(
      { mode: autopilotMode, contacts: autopilotContacts, blocked: blockedContacts, since: autopilotSince, channelPaused },
      { ref: chat._phoneNumber || '', createdAt: createdAtOf(chat) },
    );
  }, [autopilotMode, autopilotContacts, blockedContacts, channelPaused, autopilotSince]);

  // Check if a chat is in handoff state (transferred to human)
  const isChatHandoff = useCallback((chat) => {
    const phone = chat._phoneNumber || '';
    return handoffChats.includes(phone);
  }, [handoffChats]);

  // Filter and search chats (data comes pre-sorted from backend)
  const filteredChats = useMemo(() => {
    let result = chats || [];

    // Apply filters
    if (filter === 'unread') {
      result = result.filter(chat => chat.unreadCount > 0);
    } else if (filter === 'groups') {
      result = result.filter(chat => chat._isGroup || chatUtils.isGroup(chat.remoteJid));
    } else if (filter === 'favorites') {
      result = result.filter(chat => chat.pinned);
    } else if (filter === 'agent') {
      result = result.filter(chat => isChatAutopilot(chat));
    } else if (filter === 'handoff') {
      result = result.filter(chat => isChatHandoff(chat));
    }

    // Filtro por etiqueta/funil é server-side agora (Inbox refaz o fetch com
    // label_ids) — a lista `chats` já chega filtrada e paginada. Aqui só
    // restam status + busca, que operam sobre a página carregada.

    // Apply search (com filtro ativo e a flag, o servidor ja aplicou o termo)
    if (searchTerm && !buscaNosFiltros) {
      const term = searchTerm.toLowerCase().trim();
      result = result.filter(chat => {
        const name = chatUtils.getChatDisplayName(chat).toLowerCase();
        const phone = (chat._phoneNumber || '').toLowerCase();
        // Por PADRÃO a busca é só nome+telefone. O texto da mensagem (preview)
        // só entra quando o operador liga "buscar nas mensagens" no filtro.
        if (name.includes(term) || phone.includes(term)) return true;
        // Termo com 8+ dígitos: compara só os dígitos, com e sem 55 e 9º
        // dígito ("34 99160-9492" acha 553491609492). Grupo não é telefone.
        const isGroupChat = chat._isGroup || chatUtils.isGroup(chat.remoteJid);
        if (!isGroupChat && matchesPhoneSearch(chat._phoneNumber || chat.remoteJid, term)) return true;
        if (searchInMessages) {
          const lastMsg = chatUtils.getMessagePreview(chat).toLowerCase();
          return lastMsg.includes(term);
        }
        return false;
      });
      // Mescla resultados do backend (contatos fora da página carregada) que
      // ainda não estão na lista — deduplicado por telefone/jid.
      if (serverResults.length) {
        const have = new Set(result.map((c) => c._phoneNumber || c.remoteJid));
        for (const r of serverResults) {
          const key = r._phoneNumber || r.remoteJid;
          if (key && !have.has(key)) { result = result.concat(r); have.add(key); }
        }
      }
    }

    // Garante que o selecionado nao seja excluido por filtros/busca enquanto
    // o operador o esta lendo. Sem pin: ordem natural por data preservada.
    // O auto-scroll abaixo mantem o chat visivel na viewport.
    if (selectedChat?.remoteJid) {
      const inResult = result.some((c) => c.remoteJid === selectedChat.remoteJid);
      if (!inResult) {
        const fromAll = (chats || []).find((c) => c.remoteJid === selectedChat.remoteJid);
        if (fromAll) result.push(fromAll);
      }
    }

    return result;
  }, [chats, filter, searchTerm, searchInMessages, serverResults, isChatAutopilot, selectedChat, buscaNosFiltros]);

  // Etiquetas SÓ dos contatos carregados na lista.
  //
  // Antes isto vinha do /contacts-by-label, que devolve o KANBAN INTEIRO: 18,3s
  // e 13 MB para o admin da myCar (25.565 cards) só para pintar o chip de ~30
  // linhas. Em rede ruim a resposta não completava e o chip nunca aparecia.
  // O endpoint em lote resolve pela MESMA regra do cartão do contato, então o
  // chip da lista e a etapa do cartão concordam por construção.
  // B5 (2026-09-22): chave sobre `chats` + resultados da busca, não sobre
  // `filteredChats`. O filtro local muda a cada tecla e refazia este lote (uma
  // consulta por telefone no servidor) para um subconjunto que já tinha chip;
  // é o mesmo desenho do Instagram e do Messenger (refsKey).
  const phonesKey = useMemo(
    () => Array.from(new Set(
      [...(chats || []), ...(Array.isArray(serverResults) ? serverResults : [])]
        .map((c) => (c._phoneNumber || '').replace(/\D/g, '')).filter(Boolean)
    )).sort().join(','),
    [chats, serverResults],
  );
  useEffect(() => {
    if (!phonesKey) return undefined;
    let cancelado = false;
    // Debounce: a lista muda a cada página carregada e quando a busca
    // server-side devolve contatos novos.
    const t = setTimeout(async () => {
      try {
        const mapa = await authService.getContactsLabelsBatch(phonesKey.split(','));
        if (cancelado) return;
        setLabelsByPhone((prev) => {
          const next = { ...prev };
          Object.entries(mapa || {}).forEach(([fone, ids]) => {
            // Indexa as duas variantes do 9º dígito: a linha do inbox alterna
            // entre 55DDD9XXXXXXXX e 55DDDXXXXXXXX (fica a do chat mais recente).
            brPhoneVariants(fone).forEach((v) => { next[v] = ids; });
          });
          return next;
        });
      } catch (err) {
        // Lookup indisponível (503): mantém o que já está na tela em vez de
        // apagar os chips.
        console.warn('ChatList: failed to load contact labels', err);
      }
    }, 250);
    return () => { cancelado = true; clearTimeout(t); };
  }, [phonesKey]);

  // Auto-scroll para manter o selecionado visivel quando outros chats sobem
  // por NEW_MESSAGE. Usa scrollToItem('smart') que so age se item nao esta
  // visivel (sem mexer se ja esta no meio da viewport).
  useEffect(() => {
    if (!selectedChat?.remoteJid) {
      lastSelectedIndexRef.current = -1;
      return;
    }
    const idx = filteredChats.findIndex((c) => c.remoteJid === selectedChat.remoteJid);
    if (idx === -1) {
      lastSelectedIndexRef.current = -1;
      return;
    }
    if (idx !== lastSelectedIndexRef.current) {
      windowRef.current?.scrollToItem?.(idx, 'smart');
      lastSelectedIndexRef.current = idx;
    }
  }, [filteredChats, selectedChat]);

  // Get message icon
  const getMessageIcon = useCallback((msg) => {
    if (!msg || typeof msg !== 'object') return null;
    const style = { flexShrink: 0, opacity: 0.7 };
    
    if (msg.imageMessage) return <Image size={15} style={style} />;
    if (msg.videoMessage) return <Video size={15} style={style} />;
    if (msg.audioMessage) return <Mic size={15} style={style} />;
    if (msg.documentMessage) return <FileText size={15} style={style} />;
    if (msg.stickerMessage) return <Sticker size={15} style={style} />;
    if (msg.contactMessage) return <User size={15} style={style} />;
    if (msg.locationMessage) return <MapPin size={15} style={style} />;
    if (msg.pollCreationMessage) return <BarChart3 size={15} style={style} />;

    return null;
  }, []);

  // Get message status icon
  const getMessageStatus = useCallback((chat) => {
    const lastMsg = chat.lastMessage;
    if (!lastMsg?.key?.fromMe) return null;
    
    const status = lastMsg.status;
    
    if (status === 'READ' || status === 'read') {
      return <CheckCheck size={16} style={{ color: '#53bdeb', flexShrink: 0 }} />;
    }
    if (status === 'DELIVERY_ACK' || status === 'delivered') {
      return <CheckCheck size={16} style={{ color: colors.textTertiary, flexShrink: 0 }} />;
    }
    if (status === 'SERVER_ACK' || status === 'sent') {
      return <Check size={16} style={{ color: colors.textTertiary, flexShrink: 0 }} />;
    }
  }, [colors]);

  // Check if chat is selected
  const isChatSelected = useCallback((chat) => {
    return selectedChat && chatUtils.isSameChat(chat, selectedChat);
  }, [selectedChat]);

  // Filter options
  const filterOptions = [
    { id: 'all', label: 'Tudo' },
    { id: 'unread', label: 'Não lidas' },
    { id: 'handoff', label: 'Transferido' },
    { id: 'agent', label: 'Atendidos pela IA' },
  ];

  // Styles
  const styles = useMemo(() => ({
    container: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      backgroundColor: colors.bgPrimary,
    },
    header: {
      padding: '10px 14px',
      borderBottom: `1px solid ${colors.borderColor}`,
    },
    searchWrapper: {
      display: 'flex',
      alignItems: 'center',
      backgroundColor: colors.bgTertiary,
      borderRadius: '24px',
      padding: '0 12px',
      marginBottom: '10px',
      transition: 'box-shadow 0.2s',
      ...(searchFocused && { boxShadow: `0 0 0 2px ${colors.accentPrimary}40` }),
    },
    searchInput: {
      flex: 1,
      border: 'none',
      background: 'transparent',
      padding: '10px 12px',
      // 16px evita o zoom automático do iOS ao focar (causava a página
      // "deslizante"/modo zoom no inbox mobile).
      fontSize: '16px',
      color: colors.textPrimary,
      outline: 'none',
    },
    filtersContainer: {
      display: 'flex',
      gap: '6px',
      overflowX: 'auto',
      paddingBottom: '2px',
      alignItems: 'center',
    },
    // Padrao alinhado com ActionChip (Traduzir / Chamar Agente) e com o
    // toggle "Ativar Agente" no header do ChatWindow.
    filterBtn: {
      padding: '6px 12px',
      border: 'none',
      backgroundColor: colors.bgTertiary,
      color: colors.textSecondary,
      borderRadius: '18px',
      fontSize: '12px',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.2s',
      whiteSpace: 'nowrap',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
    },
    filterBtnActive: {
      backgroundColor: colors.accentPrimary,
      color: 'white',
    },
    archiveRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      padding: '12px 20px',
      cursor: 'pointer',
      borderBottom: `1px solid ${colors.borderColor}`,
    },
    listContainer: {
      flex: 1,
      overflowY: 'auto',
      overflowX: 'hidden',
    },
    chatItem: {
      display: 'flex',
      alignItems: 'center',
      padding: '10px 14px',
      cursor: 'pointer',
      transition: 'background-color 0.1s',
    },
    avatar: {
      width: '50px',
      height: '50px',
      borderRadius: '50%',
      overflow: 'hidden',
      marginRight: '12px',
      flexShrink: 0,
    },
    avatarPlaceholder: {
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.bgTertiary,
      color: colors.textTertiary,
    },
    chatInfo: {
      flex: 1,
      minWidth: 0,
      paddingBottom: '10px',
    },
    chatHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '4px',
    },
    chatName: {
      fontWeight: 500,
      fontSize: '17px',
      color: colors.textPrimary,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: '70%',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
    },
    chatTime: {
      fontSize: '12px',
      color: colors.textTertiary,
      flexShrink: 0,
    },
    chatPreview: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      fontSize: '14px',
      color: colors.textSecondary,
    },
    chatPreviewText: {
      flex: 1,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    chatBadge: {
      backgroundColor: colors.accentPrimary,
      color: 'white',
      fontSize: '11px',
      fontWeight: 600,
      minWidth: '20px',
      height: '20px',
      padding: '0 6px',
      borderRadius: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginLeft: '8px',
    },
    emptyState: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '60px 24px',
      color: colors.textTertiary,
      textAlign: 'center',
    },
    skeleton: {
      display: 'flex',
      alignItems: 'center',
      padding: '10px 14px',
    },
    skeletonAvatar: {
      width: '50px',
      height: '50px',
      borderRadius: '50%',
      backgroundColor: colors.bgTertiary,
      marginRight: '12px',
      animation: 'pulse 1.5s infinite',
    },
    skeletonLine: {
      height: '14px',
      borderRadius: '4px',
      backgroundColor: colors.bgTertiary,
      marginBottom: '8px',
      animation: 'pulse 1.5s infinite',
    },
  }), [colors, searchFocused]);

  // Dados das linhas do FixedSizeList (itemData). Memoizado: o ChatRow so
  // re-renderiza quando algo daqui muda (mensagem nova troca `chats` e,
  // portanto, `filteredChats`; selecao troca isChatSelected/selectedChatId;
  // etiqueta/responsavel trocam os mapas). Callbacks e mapas do pai entram
  // aqui por identidade, entao precisam ser estaveis (useCallback/useMemo).
  const rowData = useMemo(() => ({
    filteredChats, hasMore, loadingMore, onLoadMore, colors, styles,
    isChatSelected, labelsByPhone, isChatHandoff, getAssignment,
    assignmentsLoading, isChatAutopilot, labelsById, funnels, onSelectChat,
    activeAgentName, getMessageStatus, getMessageIcon,
    onHoverChat,
  }), [
    filteredChats, hasMore, loadingMore, onLoadMore, colors, styles,
    isChatSelected, labelsByPhone, isChatHandoff, getAssignment,
    assignmentsLoading, isChatAutopilot, labelsById, funnels, onSelectChat,
    activeAgentName, getMessageStatus, getMessageIcon,
    onHoverChat,
  ]);

  return (
    <div style={styles.container}>
      {/* Header with Search and Filters */}
      <div style={styles.header}>
        <div style={styles.searchWrapper}>
          <Search
            size={18}
            style={{ color: searchFocused ? colors.accentPrimary : colors.textTertiary }}
          />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Pesquisar ou começar nova conversa"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            style={styles.searchInput}
          />
          {searching && (
            <Loader2
              size={16}
              style={{ color: colors.accentPrimary, animation: 'chatlist-spin 0.8s linear infinite', flexShrink: 0 }}
            />
          )}
          {searchTerm && (
            <button
              style={{ padding: '4px', border: 'none', background: 'transparent', color: colors.textTertiary, cursor: 'pointer' }}
              onClick={() => setSearchTerm('')}
            >
              ×
            </button>
          )}
          {/* Botão + para iniciar nova conversa por número */}
          <button
            type="button"
            onClick={() => setShowNewConv(true)}
            title="Nova conversa"
            aria-label="Nova conversa"
            style={{
              flexShrink: 0, width: 30, height: 30, borderRadius: '50%',
              border: 'none', backgroundColor: colors.accentPrimary, color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
            }}
          >
            <Plus size={18} />
          </button>
        </div>

        {/* Criar contato = MESMO drawer dos detalhes, em modo criar (telefone
            editável + tags/comentários/campos; sem followups/"Mais informações").
            Renderiza via portal, então não fica preso ao container da lista. */}
        <ContactDetailsDrawer
          isOpen={showNewConv}
          onClose={() => setShowNewConv(false)}
          colors={colors}
          source="wa_personal"
          createMode
          onCreate={handleNewConvSubmit}
        />

        {/* Atalhos rapidos inline (Tudo / Nao lidas) + pilula "Funil" (abre o
            drawer na secao de funil) + botao "Filtrar" (drawer no topo). */}
        {searchTerm && serverHasMore && !buscaNosFiltros && (
          <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 6 }}>
            Mostrando os 40 primeiros resultados da busca. Refine o termo para ver os outros.
          </div>
        )}
        {(filter === 'handoff' || filter === 'agent') && hasMore && !searchTerm && (
          <div style={{ fontSize: 11, color: colors.textSecondary, marginTop: 6 }}>
            Este filtro olha só as conversas já carregadas. Role a lista para carregar mais.
          </div>
        )}
        <div style={styles.filtersContainer}>
          {filterOptions
            .filter(f => f.id === 'all' || f.id === 'unread')
            .map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                style={{
                  ...styles.filterBtn,
                  ...(filter === f.id ? styles.filterBtnActive : {}),
                }}
              >
                {f.label}
              </button>
            ))}
          {/* Pilula "Funil": abre o drawer ja rolando/expandindo o funil. */}
          <button
            onClick={() => { setDrawerFocus('funnel'); setShowFilterDrawer(true); }}
            style={{
              ...styles.filterBtn,
              ...(selectedLabelFilters.size > 0 ? styles.filterBtnActive : {}),
            }}
            title="Filtrar por funil"
          >
            <GitBranch size={12} />
            Funil
            {selectedLabelFilters.size > 0 && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                minWidth: 16, height: 16, padding: '0 5px',
                borderRadius: 8, fontSize: 10, fontWeight: 700,
                backgroundColor: 'rgba(255,255,255,0.25)',
              }}>
                {selectedLabelFilters.size}
              </span>
            )}
          </button>
          {(() => {
            // Considera ativo qualquer status que NAO seja inline
            // ("Atendidos pela IA") + etiquetas selecionadas.
            const drawerStatusActive = filter !== 'all' && filter !== 'unread';
            const extraActive = (filterMember ? 1 : 0) + (filterSince ? 1 : 0) + ((filterCreated.from || filterCreated.to) ? 1 : 0) + (filterUnanswered ? 1 : 0);
            // Etiquetas contam como 1 grupo (igual ao rodapé do drawer) — o nº de
            // etapas selecionadas já aparece na pílula "Funil".
            const totalActive = (drawerStatusActive ? 1 : 0) + (selectedLabelFilters.size > 0 ? 1 : 0) + extraActive;
            return (
              <button
                onClick={() => { setDrawerFocus(null); setShowFilterDrawer(true); }}
                style={{
                  ...styles.filterBtn,
                  ...(totalActive > 0 ? styles.filterBtnActive : {}),
                }}
                title="Filtrar conversas"
              >
                <Filter size={12} />
                Filtrar
                {totalActive > 0 && (
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    minWidth: 16, height: 16, padding: '0 5px',
                    borderRadius: 8, fontSize: 10, fontWeight: 700,
                    backgroundColor: 'rgba(255,255,255,0.25)',
                  }}>
                    {totalActive}
                  </span>
                )}
              </button>
            );
          })()}
        </div>

        {/* Disclaimer compacto quando ha filtros ativos. */}
        {(filter !== 'all' || selectedLabelFilters.size > 0 || filterMember || filterSince || filterCreated.from || filterCreated.to || filterUnanswered) && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            marginTop: 8, fontSize: 11, color: colors.textSecondary,
            flexWrap: 'wrap',
          }}>
            <span>Filtrando por:</span>
            {filter !== 'all' && (() => {
              const statusOpt = filterOptions.find(o => o.id === filter);
              return statusOpt ? (
                <span style={{
                  color: colors.accentPrimary, fontWeight: 600,
                }}>
                  {statusOpt.label}
                </span>
              ) : null;
            })()}
            {Array.from(selectedLabelFilters).map((id) => {
              const label = labelsById[id];
              if (!label) return null;
              return (
                <span key={id} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  color: label.color, fontWeight: 600,
                }}>
                  <span style={{
                    width: 6, height: 6, borderRadius: '50%',
                    backgroundColor: label.color,
                  }} />
                  {label.name}
                </span>
              );
            })}
            {filterMember && (() => {
              const t = assignTargets.find((m) => m.id === filterMember);
              return (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: colors.accentPrimary, fontWeight: 600 }}>
                  {t?.type === 'agent' && <Bot size={12} />}
                  {t?.name || 'Responsável'}
                </span>
              );
            })()}
            {filterSince && (
              <span style={{ color: colors.accentPrimary, fontWeight: 600 }}>
                {filterSince === 'today' ? 'Hoje' : filterSince === '7d' ? 'Últimos 7 dias' : 'Últimos 30 dias'}
              </span>
            )}
            {(filterCreated.from || filterCreated.to) && (
              <span style={{ color: colors.accentPrimary, fontWeight: 600 }}>
                Entrou{filterCreated.from ? ` de ${filterCreated.from.split('-').reverse().join('/')}` : ''}{filterCreated.to ? ` até ${filterCreated.to.split('-').reverse().join('/')}` : ''}
              </span>
            )}
            {filterUnanswered && (
              <span style={{ color: colors.accentPrimary, fontWeight: 600 }}>Sem resposta</span>
            )}
            <button
              onClick={() => {
                setFilter('all');
                handleChangeLabelFilters(new Set());
                setFilterMember(''); setFilterSince(''); setFilterCreated({ from: '', to: '' }); setFilterUnanswered(false);
                onExtraFiltersChange?.({ member_id: null, since: null, created_from: null, created_to: null, unanswered: false });
              }}
              style={{
                background: 'transparent', border: 'none',
                color: colors.textTertiary, fontSize: 11, cursor: 'pointer',
                textDecoration: 'underline', padding: 0,
              }}
            >
              limpar
            </button>
          </div>
        )}
      </div>

      <ChatFilterDrawer
        isOpen={showFilterDrawer}
        onClose={() => { setShowFilterDrawer(false); setDrawerFocus(null); }}
        colors={colors}
        initialFocusSection={drawerFocus}
        statusOptions={filterOptions.filter(f => f.id !== 'all' && f.id !== 'unread')}
        statusValue={filter}
        onChangeStatus={setFilter}
        labels={Object.values(labelsById)}
        funnels={funnels}
        selectedLabelIds={selectedLabelFilters}
        onChangeLabels={handleChangeLabelFilters}
        members={assignTargets}
        memberValue={filterMember}
        onChangeMember={handleChangeMember}
        sinceValue={filterSince}
        onChangeSince={handleChangeSince}
        createdFromValue={filterCreated.from}
        createdToValue={filterCreated.to}
        onChangeCreatedRange={handleChangeCreatedRange}
        unansweredValue={filterUnanswered}
        onChangeUnanswered={handleChangeUnanswered}
        onClearExtra={handleClearExtra}
        searchInMessages={searchInMessages}
        onChangeSearchInMessages={setSearchInMessages}
        customFields={campos.fields}
        customFieldValues={campos.values}
        onChangeCustomFields={(vals) => {
          campos.setValues(vals);
          notifyExtraFilters({ customFields: vals });
        }}
      />

      {/* Results count when searching */}
      {searchTerm && (
        <div style={{ padding: '8px 14px', fontSize: '12px', color: colors.textTertiary, borderBottom: `1px solid ${colors.borderColor}` }}>
          {filteredChats.length} conversa{filteredChats.length !== 1 ? 's' : ''} encontrada{filteredChats.length !== 1 ? 's' : ''}
        </div>
      )}

      {/* Chat Items */}
      <div ref={listRef} style={styles.listContainer}>
        {loading ? (
          // Skeleton loading — preserved from before. Coexists with virtualization.
          [...Array(6)].map((_, i) => (
            <div key={i} style={styles.skeleton}>
              <div style={styles.skeletonAvatar} />
              <div style={{ flex: 1 }}>
                <div style={{ ...styles.skeletonLine, width: '65%' }} />
                <div style={{ ...styles.skeletonLine, width: '45%', height: '12px' }} />
              </div>
            </div>
          ))
        ) : filteredChats.length === 0 ? (
          // Empty state
          <div style={styles.emptyState}>
            <div style={{ width: 80, height: 80, backgroundColor: colors.bgTertiary, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
              <Search size={32} />
            </div>
            <p style={{ fontSize: 16, fontWeight: 500, color: colors.textPrimary, marginBottom: 8 }}>
              {searchTerm ? 'Nenhuma conversa encontrada' : 'Nenhuma conversa'}
            </p>
            <p style={{ fontSize: 14 }}>
              {searchTerm ? 'Tente buscar por outro nome ou número' : 'Suas conversas aparecerão aqui'}
            </p>
          </div>
        ) : (
          // Virtualized chat list
          <FixedSizeList
            ref={windowRef}
            height={listHeight}
            itemCount={filteredChats.length + (hasMore ? 1 : 0)}
            itemSize={ROW_HEIGHT}
            itemData={rowData}
            width="100%"
            initialScrollOffset={initialScrollOffset}
            onScroll={onScrollOffset ? ({ scrollOffset }) => onScrollOffset(scrollOffset) : undefined}
          >
            {ChatRow}
          </FixedSizeList>
        )}
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes chatlist-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        input::placeholder { color: ${colors.textTertiary}; }
      `}</style>
    </div>
  );
};

export default ChatList;